import { Suspense, lazy, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import type { OrderFormHandle } from "@/components/OrderForm";
import DarkModeToggle from "@/components/DarkModeToggle";
import { optimizedImage } from "@/lib/imageOptimize";
import StorefrontThemeSwitcher from "@/components/StorefrontThemeSwitcher";
import { OrderFormSkeleton, FaqSkeleton, ReviewsSkeleton } from "@/components/StorefrontSkeletons";
import { Smartphone, UserCircle, HelpCircle, ArrowLeftRight, Package } from "lucide-react";
import { useFaqStore } from "@/stores/faqStore";
import { useAboutStore } from "@/stores/aboutStore";
import { useAdminSettings } from "@/stores/adminStore";
import { useStorefrontSettings } from "@/stores/storefrontSettingsStore";
import { useTenantStore } from "@/lib/tenantContext";
import { useSwapStore } from "@/stores/swapStore";
import { useSync } from "@/components/CloudSyncProvider";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const DealOfTheWeek = lazy(() => import("@/components/DealOfTheWeek"));
const FlashSalePopup = lazy(() => import("@/components/FlashSalePopup"));
const TrustBadges = lazy(() => import("@/components/TrustBadges"));
const OrderForm = lazy(() => import("@/components/OrderForm"));
const AdSlotRenderer = lazy(() => import("@/components/AdSlotRenderer"));
const SponsoredProductsRail = lazy(() => import("@/components/SponsoredProductsRail"));
const FeaturedDevice = lazy(() => import("@/components/FeaturedDevice"));
const CustomerReviews = lazy(() => import("@/components/CustomerReviews"));
const InvalidReferralContact = lazy(() => import("@/components/InvalidReferralContact"));
const StorefrontSearch = lazy(() => import("@/components/StorefrontSearch"));
const TutorialVideoWidget = lazy(() => import("@/components/TutorialVideoWidget"));

interface Props {
  referralStaff?: string;
  referralInvalid: boolean;
  isStaffLocked: boolean;
  showContactOnly: boolean;
  staffCommissionPercent: number;
  rawRef?: string;
}

export default function ClassicStorefront({
  referralStaff,
  referralInvalid,
  isStaffLocked,
  showContactOnly,
  staffCommissionPercent,
  rawRef,
}: Props) {
  const allFaqs = useFaqStore(s => s.faqs);
  const faqs = useMemo(() => [...allFaqs].filter(f => f.enabled).sort((a, b) => a.order - b.order), [allFaqs]);
  const aboutRaw = useAboutStore(s => s.data);
  const aboutData = useMemo(() => ({ ...aboutRaw, members: [...aboutRaw.members].sort((a, b) => a.order - b.order) }), [aboutRaw]);
  const adminSettings = useAdminSettings();
  const sfSettings = useStorefrontSettings();
  const tenant = useTenantStore(s => s.tenant);
  const headerTitle = tenant?.store_title || sfSettings.siteTitle || "Khay's Phones & Premium Devices Pre-Order";
  const headerTagline = tenant?.hero_heading || tenant?.description || sfSettings.siteTagline || "Quality-inspected, professionally graded, competitively priced — so you always know exactly what you're getting.";
  const swapSettings = useSwapStore(s => s.settings);
  const { isLoaded } = useSync();
  const orderFormRef = useRef<OrderFormHandle>(null);

  const handleSearchSelect = useCallback((deviceName: string, category: string) => {
    orderFormRef.current?.selectDevice(category, deviceName);
  }, []);

  // Full-screen lockdown overlay (item 10).
  if (showContactOnly) {
    return (
      <div className="min-h-screen-safe bg-background text-foreground flex items-center justify-center px-4 py-8">
        <main className="w-full max-w-xl" aria-label="Storefront access request">
          <Suspense fallback={<div className="min-h-48 rounded-2xl bg-card border border-border/50" />}>
            <InvalidReferralContact
              whatsappNumber={adminSettings.whatsappNumber || "233598429052"}
              reason={referralInvalid ? "invalid_referral" : "access_restricted"}
              showReferrer={referralInvalid || isStaffLocked || !!rawRef}
            />
          </Suspense>
        </main>
      </div>
    );
  }

  return (
    <Suspense fallback={<div className="min-h-screen bg-background" />}>
      <div className="min-h-screen-safe bg-background flex flex-col">
        <StorefrontThemeSwitcher />
        <TutorialVideoWidget />
        {!showContactOnly && isLoaded && <FlashSalePopup />}
        {!showContactOnly && isLoaded && <DealOfTheWeek referralStaff={referralStaff} staffCommission={0} />}
        <AdSlotRenderer position="before-hero" />

        <header className="header-solid py-6 sm:py-8 px-4 relative" aria-label="Site header">
          <div className="absolute top-3 right-3">
            <DarkModeToggle compact />
          </div>
          <div className="max-w-xl mx-auto text-center">
            <div className="w-10 h-10 bg-primary-foreground/15 rounded-xl flex items-center justify-center mx-auto mb-3 backdrop-blur-sm">
              <Smartphone className="w-5 h-5 text-primary-foreground" />
            </div>
            <h1 className="font-display text-lg sm:text-xl md:text-2xl font-bold text-primary-foreground leading-tight mb-2 drop-shadow-sm">
              {headerTitle}
            </h1>
            <p className="text-primary-foreground/80 text-xs sm:text-sm max-w-md mx-auto leading-relaxed">
              {headerTagline}
            </p>
          </div>
        </header>

        <AdSlotRenderer position="after-hero" priority />

        <main className="flex-1 px-3 sm:px-4 py-5 md:py-8 max-w-xl mx-auto w-full space-y-4" aria-label="Storefront">
          <h2 className="sr-only">Configure and pre-order your device</h2>
          {showContactOnly ? (
            <>
              <AdSlotRenderer position="before-trust" />
              <AdSlotRenderer position="after-trust" />
              <AdSlotRenderer position="before-form" />
              <InvalidReferralContact
                whatsappNumber={adminSettings.whatsappNumber || "233598429052"}
                reason={referralInvalid ? "invalid_referral" : "access_restricted"}
                showReferrer={referralInvalid || isStaffLocked || !!rawRef}
              />
              <AdSlotRenderer position="after-form" />
              <AdSlotRenderer position="before-faq" />
              <AdSlotRenderer position="after-faq" />
            </>
          ) : (
            <>
              <div className="space-y-3">
                <div className="rounded-xl bg-card border border-border/50 p-3 flex gap-2">
                  <Link to="/track" className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all bg-primary/10 text-primary hover:bg-primary/20">
                    <Package className="w-3.5 h-3.5" />
                    Track Order
                  </Link>
                  {swapSettings.enabled && (() => {
                    const btnStyle: React.CSSProperties = {};
                    if (swapSettings.buttonColor) {
                      if (swapSettings.buttonEffect === "gradient") {
                        btnStyle.background = `linear-gradient(135deg, ${swapSettings.buttonColor}, ${swapSettings.buttonGradientEnd || "#4a4a8a"})`;
                      } else {
                        btnStyle.backgroundColor = swapSettings.buttonColor;
                      }
                    }
                    if (swapSettings.buttonTextColor) btnStyle.color = swapSettings.buttonTextColor;
                    if (swapSettings.buttonEffect === "glow" && swapSettings.buttonColor) {
                      btnStyle.boxShadow = `0 0 20px ${swapSettings.buttonColor}60`;
                    }
                    const hasCustomStyle = !!swapSettings.buttonColor;
                    return (
                      <Link
                        to="/swap"
                        className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all ${!hasCustomStyle ? "bg-accent text-accent-foreground hover:bg-accent/80" : "hover:opacity-90"}`}
                        style={hasCustomStyle ? btnStyle : undefined}
                      >
                        <ArrowLeftRight className="w-3.5 h-3.5" />
                        {swapSettings.pageLabel || "Swap Device"}
                      </Link>
                    );
                  })()}
                </div>
                <div className="rounded-xl bg-card border border-border/50 p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">Configure Your Device</p>
                  <p className="text-[10px] text-muted-foreground">Select a category below to begin</p>
                </div>
              </div>

              <StorefrontSearch onSelect={handleSearchSelect} />
              <AdSlotRenderer position="before-trust" />
              <TrustBadges />
              <AdSlotRenderer position="after-trust" />

              <AdSlotRenderer position="before-form" />
              {!isLoaded ? <OrderFormSkeleton /> : <OrderForm ref={orderFormRef} referralStaff={referralStaff} staffCommissionPercent={staffCommissionPercent} />}
              <AdSlotRenderer position="after-form" />
              <SponsoredProductsRail className="pt-2" />

              {!isLoaded ? (
                adminSettings.reviewsOnStorefront ? <ReviewsSkeleton /> : null
              ) : (
                adminSettings.reviewsOnStorefront && <CustomerReviews showAll maxItems={8} />
              )}

              {!isLoaded ? (
                sfSettings.faqEnabled ? <FaqSkeleton /> : null
              ) : (
                sfSettings.faqEnabled && faqs.length > 0 && (
                  <>
                    <AdSlotRenderer position="before-faq" />
                    <section className="bg-card rounded-2xl shadow-card p-4 sm:p-6 border border-border/50">
                      <div className="flex items-center gap-2 mb-3">
                        <HelpCircle className="w-4 h-4 text-primary" />
                        <h2 className="font-display text-base font-bold text-card-foreground">{sfSettings.faqTitle}</h2>
                      </div>
                      <Accordion type="single" collapsible className="w-full">
                        {faqs.map((faq) => (
                          <AccordionItem key={faq.id} value={faq.id}>
                            <AccordionTrigger className="text-xs font-semibold text-foreground text-left">{faq.question}</AccordionTrigger>
                            <AccordionContent>
                              {faq.answer ? (
                                <p className="text-xs text-muted-foreground">{faq.answer}</p>
                              ) : (
                                <p className="text-xs text-muted-foreground italic">Answer coming soon.</p>
                              )}
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </section>
                    <AdSlotRenderer position="after-faq" />
                  </>
                )
              )}
            </>
          )}
        </main>

        <AdSlotRenderer position="before-footer" />

        <footer className="py-6 px-4 border-t border-border bg-card" aria-label="About and site footer">
          {!showContactOnly && (aboutData.storeDescription || aboutData.members.length > 0) && (
            <div className="max-w-xl mx-auto mb-5">
              <div className="flex items-center gap-2 mb-3 justify-center">
                <UserCircle className="w-4 h-4 text-primary" />
                <h2 className="font-display text-sm font-bold text-card-foreground">About Us</h2>
              </div>
              {aboutData.storeDescription && (
                <p className="text-xs text-muted-foreground text-center mb-4">{aboutData.storeDescription}</p>
              )}
              {aboutData.members.length > 0 && (() => {
                const groups = aboutData.groups || [];
                const membersByGroup: Record<string, typeof aboutData.members> = {};
                const ungrouped: typeof aboutData.members = [];
                aboutData.members.forEach(m => {
                  if (m.group && groups.includes(m.group)) {
                    if (!membersByGroup[m.group]) membersByGroup[m.group] = [];
                    membersByGroup[m.group].push(m);
                  } else {
                    ungrouped.push(m);
                  }
                });
                const renderMember = (member: typeof aboutData.members[0]) => (
                  <div key={member.id} className="text-center space-y-1.5">
                    <div className="w-14 h-14 rounded-full bg-accent flex items-center justify-center mx-auto overflow-hidden">
                      {member.imageUrl ? (
                        <img src={optimizedImage(member.imageUrl, { width: 56, height: 56, resize: "cover" })} alt={member.name} loading="lazy" width={56} height={56} className="w-full h-full object-cover" />
                      ) : (
                        <UserCircle className="w-8 h-8 text-muted-foreground" />
                      )}
                    </div>
                    <div>
                      <p className="text-xs font-bold text-foreground">{member.name}</p>
                      <p className="text-[10px] text-primary font-medium">{member.role}</p>
                      {member.bio && <p className="text-[10px] text-muted-foreground mt-0.5">{member.bio}</p>}
                    </div>
                  </div>
                );
                return (
                  <details open={typeof window !== "undefined" && window.matchMedia("(min-width: 640px)").matches} className="group rounded-xl border border-border/50 bg-background/50 overflow-hidden">
                    <summary className="flex items-center justify-center gap-2 px-3 py-2.5 cursor-pointer list-none select-none hover:bg-accent/50 transition-colors [&::-webkit-details-marker]:hidden">
                      <UserCircle className="w-3.5 h-3.5 text-primary" />
                      <span className="text-xs font-bold text-foreground">Meet the Team</span>
                      <span className="text-[10px] text-muted-foreground">({aboutData.members.length})</span>
                      <svg className="w-3 h-3 text-muted-foreground transition-transform group-open:rotate-180" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
                    </summary>
                    <div className="p-3 space-y-4">
                      {groups.map(g => {
                        const members = membersByGroup[g];
                        if (!members || members.length === 0) return null;
                        return (
                          <div key={g}>
                            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide text-center mb-2">{g}</p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{members.map(renderMember)}</div>
                          </div>
                        );
                      })}
                      {ungrouped.length > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{ungrouped.map(renderMember)}</div>
                      )}
                    </div>
                  </details>
                );
              })()}
            </div>
          )}

          <p className="text-[10px] text-muted-foreground text-center">© 2026 Khay's Phones and Premium Devices Pre-Order LTD. All prices in Ghana Cedis (GH₵).</p>
        </footer>

        {!showContactOnly && <FeaturedDevice />}
      </div>
    </Suspense>
  );
}